import { createReducer, on } from '@ngrx/store';
import { ResetStateFromRouter } from 'src/app/shared/actions/eliqua.actions';
import { EditCampaignSuccess } from '../actions/campaign-search.actions';
import { CampaignSelectionTargetChanged } from '../actions/wizard-setup.actions';
import {
  AddOnceOffDate,
  AddSchedule,
  HelpTextChanged,
  RemoveOnceOffDate,
  RemoveSchedule,
  SetPendingOnceOffDate,
  TimezoneChanged,
  UpdateSchedule,
  WhenTypeChanged,
} from '../actions/wizard-when.actions';
import { ResetWizard } from '../actions/wizard.actions';
import { generateOnceOffDTO } from '../components/wizard/steps/when/utils/once-off.mapper';
import { generateScheduleDTO } from '../components/wizard/steps/when/utils/schedule.mapper';
import { CampaignWhenDTO, ScheduleConfig, WizardWhenState } from '../models/wizard.model';

const defaultSchedule: ScheduleConfig = {
  frequency: '',
  minute: '',
  hour: '',
  dayOfWeek: '',
  dayOfMonth: '',
  month: '',
};

const initialCampaignWhen: CampaignWhenDTO = {
  campaignEventTypeList: null,
  campaignScheduleList: [],
  campaignOnceOffList: [],
};

const initialState: WizardWhenState = {
  helpTextStatus: false,
  selectedWhenType: 'manual',
  selectedTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  schedules: [],
  onceOffDates: [],
  pendingOnceOffDate: null,
  campaignWhen: { ...initialCampaignWhen },
};

export const WizardWhenReducer = createReducer(
  initialState,
  on(HelpTextChanged, (state) => ({
    ...state,
    helpTextStatus: !state.helpTextStatus,
  })),
  on(WhenTypeChanged, (state, { whenType }) => ({
    ...state,
    selectedWhenType: whenType,
    schedules: [],
    onceOffDates: [],
    campaignWhen: {
      campaignEventTypeList: [],
      campaignScheduleList: [],
      campaignOnceOffList: [],
    },
  })),
  on(TimezoneChanged, (state, { timezone }) => ({
    ...state,
    selectedTimezone: timezone,
    campaignWhen: {
      ...state.campaignWhen,
      campaignScheduleList: generateScheduleDTO(state.schedules, timezone),
      campaignOnceOffList: generateOnceOffDTO(state.onceOffDates, timezone),
    },
  })),
  on(AddSchedule, (state) => {
    const newSchedules = [...state.schedules, { ...defaultSchedule }];
    return {
      ...state,
      schedules: newSchedules,
      campaignWhen: {
        ...state.campaignWhen,
        campaignScheduleList: generateScheduleDTO(newSchedules, state.selectedTimezone),
      },
    };
  }),
  on(RemoveSchedule, (state, { index }) => {
    const newSchedules = state.schedules.filter((_, i) => i !== index);
    return {
      ...state,
      schedules: newSchedules,
      campaignWhen: {
        ...state.campaignWhen,
        campaignScheduleList: generateScheduleDTO(newSchedules, state.selectedTimezone),
      },
    };
  }),
  on(UpdateSchedule, (state, { index, schedule }) => {
    const newSchedules = [...state.schedules];
    newSchedules[index] = schedule;

    return {
      ...state,
      schedules: newSchedules,
      campaignWhen: {
        ...state.campaignWhen,
        campaignScheduleList: generateScheduleDTO(newSchedules, state.selectedTimezone),
      },
    };
  }),
  on(AddOnceOffDate, (state, { dateString }) => {
    const newDates = [...state.onceOffDates, dateString];
    return {
      ...state,
      onceOffDates: newDates,
      pendingOnceOffDate: null,
      campaignWhen: {
        ...state.campaignWhen,
        campaignOnceOffList: generateOnceOffDTO(newDates, state.selectedTimezone),
      },
    };
  }),
  on(RemoveOnceOffDate, (state, { index }) => {
    const newDates = state.onceOffDates.filter((_, i) => i !== index);
    return {
      ...state,
      onceOffDates: newDates,
      campaignWhen: {
        ...state.campaignWhen,
        campaignOnceOffList: generateOnceOffDTO(newDates, state.selectedTimezone),
      },
    };
  }),
  on(SetPendingOnceOffDate, (state, { date }) => ({
    ...state,
    pendingOnceOffDate: date,
  })),
  on(ResetWizard, () => ({
    ...initialState,
  })),
  on(CampaignSelectionTargetChanged, () => ({
    ...initialState,
  })),
  on(EditCampaignSuccess, (state, { campaign }) => {
    const campaignWhen = campaign.data.campaignWhen;

    if (!campaignWhen) return state;

    const onceOffDates: string[] = (campaignWhen.campaignOnceOffList || []).map((item: any) => {
      const hour = item.hour ? item.hour.trim().padStart(2, '0') : '00';
      const minute = item.minute ? item.minute.trim().padStart(2, '0') : '00';

      return `${item.dayOfMonth}-${item.month}-${item.year}, ${hour}:${minute}`;
    });

    const schedules: ScheduleConfig[] = (campaignWhen.campaignScheduleList || []).map(
      (item: any) => {
        let deducedFrequency = 'Day';

        if (item.month && item.month !== '*') {
          deducedFrequency = 'Year';
        } else if (item.dayOfMonth && item.dayOfMonth !== '*') {
          deducedFrequency = 'Month';
        } else if (item.dayOfWeek && item.dayOfWeek !== '*') {
          deducedFrequency = 'Week';
        } else if (item.hour && item.hour !== '*') {
          deducedFrequency = 'Day';
        } else {
          deducedFrequency = 'Hour';
        }

        const monthNames = [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ];
        const mappedMonth =
          item.month && item.month !== '*' ? monthNames[parseInt(item.month, 10) - 1] : '';

        let mappedDayOfMonth = '';
        if (item.dayOfMonth && item.dayOfMonth !== '*') {
          const day = parseInt(item.dayOfMonth, 10);
          if (day > 3 && day < 21) {
            mappedDayOfMonth = `${day}th`;
          } else {
            switch (day % 10) {
              case 1:
                mappedDayOfMonth = `${day}st`;
                break;
              case 2:
                mappedDayOfMonth = `${day}nd`;
                break;
              case 3:
                mappedDayOfMonth = `${day}rd`;
                break;
              default:
                mappedDayOfMonth = `${day}th`;
                break;
            }
          }
        }

        let mappedDayOfWeek = '';
        if (item.dayOfWeek && item.dayOfWeek !== '*') {
          const dowMap: { [key: string]: string } = {
            '1': 'Monday',
            '2': 'Tuesday',
            '3': 'Wednesday',
            '4': 'Thursday',
            '5': 'Friday',
            '6': 'Saturday',
            '7': 'Sunday',
            MON: 'Monday',
            TUE: 'Tuesday',
            WED: 'Wednesday',
            THU: 'Thursday',
            FRI: 'Friday',
            SAT: 'Saturday',
            SUN: 'Sunday',
          };
          mappedDayOfWeek = dowMap[item.dayOfWeek.toString().toUpperCase()] || item.dayOfWeek;
        }

        return {
          frequency: deducedFrequency,
          minute: item.minute && item.minute !== '*' ? item.minute.trim().padStart(2, '0') : '',
          hour: item.hour && item.hour !== '*' ? item.hour.trim().padStart(2, '0') : '',
          dayOfWeek: mappedDayOfWeek,
          dayOfMonth: mappedDayOfMonth,
          month: mappedMonth,
        };
      },
    );

    let selectedWhenType = 'manual';
    if (schedules.length > 0) {
      selectedWhenType = 'schedule';
    } else if (onceOffDates.length > 0) {
      selectedWhenType = 'onceOff';
    }

    let selectedTimezone = state.selectedTimezone;
    if (
      campaignWhen.campaignScheduleList?.length > 0 &&
      campaignWhen.campaignScheduleList[0].timezone
    ) {
      selectedTimezone = campaignWhen.campaignScheduleList[0].timezone;
    } else if (
      campaignWhen.campaignOnceOffList?.length > 0 &&
      campaignWhen.campaignOnceOffList[0].timezone
    ) {
      selectedTimezone = campaignWhen.campaignOnceOffList[0].timezone;
    }

    return {
      ...state,
      campaignWhen: campaignWhen,
      selectedWhenType,
      selectedTimezone,
      onceOffDates,
      schedules,
    };
  }),
  on(ResetStateFromRouter, () => ({
    ...initialState,
  })),
);
